import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { CurrentUserData, RbacRolePermission } from '../services/rbac/rbacService';
import { useCurrentUser } from '../hooks/queries';
import { RBAC_RESOURCES } from '../config/rbac';
import { useEffectiveOid } from './useEffectiveOid';
import { classifyRbacError } from './classifyRbacError';
import { getStoredImpersonation } from '../features/impersonation/impersonationStorage';

interface RbacContextValue {
  currentUser: CurrentUserData | null;
  isLoading: boolean;
  error: string | null;
  hasPermission: (resource: string, permission: string) => boolean;
  hasAnyPermission: (resource: string, permissions: string[]) => boolean;
  hasAnyChildPermission: (resource: string, permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  reload: () => Promise<void>;
}

const RbacContext = createContext<RbacContextValue | undefined>(undefined);

export const RbacProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isReady } = useAuth();
  const effectiveOid = useEffectiveOid();

  // Эмуляция ("Войти как") стартует и завершается полной перезагрузкой страницы,
  // поэтому достаточно прочитать хранилище один раз при рендере.
  const impersonation = getStoredImpersonation();

  // Ключ кэша должен различать личности: кэш 'rbac' персистится в localStorage
  // (см. services/core/queryPersister.ts) и переживает reload, поэтому на общем
  // ключе после входа в эмуляцию восстановились бы права актора.
  const identityKey = impersonation?.targetId ?? effectiveOid;

  const queryEnabled = (isReady && isAuthenticated) || !!identityKey;
  const userQuery = useCurrentUser(identityKey, queryEnabled);
  const currentUser = userQuery.data ?? null;

  const isLoading = currentUser
    ? false
    : (!isReady || (queryEnabled ? userQuery.isLoading : false));

  const error = useMemo(() => {
    if (!queryEnabled) return null;
    if (!identityKey) return 'Azure OID не найден';
    if (!userQuery.error) return null;
    return classifyRbacError(userQuery.error);
  }, [queryEnabled, identityKey, userQuery.error]);

  const childrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    Object.values(RBAC_RESOURCES).forEach(r => {
      if ('parent' in r && r.parent) {
        if (!map.has(r.parent)) {
          map.set(r.parent, []);
        }
        map.get(r.parent)!.push(r.value);
      }
    });
    return map;
  }, []);

  const permissionsCache = useMemo(() => {
    const cache = new Set<string>();

    const addWithManage = (resource: string, permission: string) => {
      cache.add(`${resource}:${permission}`);
      if (permission === 'manage') {
        cache.add(`${resource}:read`);
        cache.add(`${resource}:write`);
        cache.add(`${resource}:delete`);
      }
    };

    currentUser?.permissions.forEach((p: RbacRolePermission) => {
      addWithManage(p.resource.value, p.permission.value);
    });

    const propagateDown = (parent: string) => {
      const children = childrenMap.get(parent);
      if (!children) return;
      for (const perm of ['read', 'write', 'delete', 'manage']) {
        if (cache.has(`${parent}:${perm}`)) {
          for (const child of children) {
            addWithManage(child, perm);
          }
        }
      }
      for (const child of children) {
        propagateDown(child);
      }
    };

    for (const parent of childrenMap.keys()) {
      propagateDown(parent);
    }

    return cache;
  }, [currentUser, childrenMap]);

  const rolesCache = useMemo(() => {
    const cache = new Set<string>();
    currentUser?.roles.forEach((r) => {
      cache.add(r.value);
    });
    return cache;
  }, [currentUser]);

  const hasPermission = useCallback(
    (resource: string, permission: string): boolean => {
      if (!currentUser) return false;
      return permissionsCache.has(`${resource}:${permission}`);
    },
    [currentUser, permissionsCache]
  );

  const hasAnyPermission = useCallback(
    (resource: string, permissions: string[]): boolean => {
      if (!currentUser) return false;
      return permissions.some(p => permissionsCache.has(`${resource}:${p}`));
    },
    [currentUser, permissionsCache]
  );

  const hasAnyChildPermission = useCallback(
    (resource: string, permission: string): boolean => {
      if (!currentUser) return false;
      if (permissionsCache.has(`${resource}:${permission}`)) return true;
      const walkChildren = (parent: string): boolean => {
        const children = childrenMap.get(parent);
        if (!children) return false;
        return children.some(
          child => permissionsCache.has(`${child}:${permission}`) || walkChildren(child)
        );
      };
      return walkChildren(resource);
    },
    [currentUser, permissionsCache, childrenMap]
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!currentUser) return false;
      return rolesCache.has(role);
    },
    [currentUser, rolesCache]
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!currentUser) return false;
      return roles.some(r => rolesCache.has(r));
    },
    [currentUser, rolesCache]
  );

  const value: RbacContextValue = {
    currentUser,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAnyChildPermission,
    hasRole,
    hasAnyRole,
    reload: async () => {
      await userQuery.refetch();
    },
  };

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useRbac(): RbacContextValue {
  const context = useContext(RbacContext);
  if (!context) {
    throw new Error('useRbac must be used within RbacProvider');
  }
  return context;
}
